// src/App.js - Refactored Version
import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, BarChart3, Code, Wrench, Shield, Zap, Palette, ListChecks, CalendarClock, Home, Video } from 'lucide-react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import ActivatePage from './pages/ActivatePage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import TeamPage from './pages/TeamPage';
import AIMPage from './pages/AIMPage';
import SchedulePage from './pages/SchedulePage';
import CustomizationPage from './pages/CustomizationPage';
import ModuleLibraryPage from './pages/ModuleLibraryPage';
import FinancialReportingPage from './pages/FinancialReportingPage';
import ClientsPage from './pages/ClientsPage';
import MyHomePage from './pages/MyHomePage';
import ArgusPage from './pages/ArgusPage';
import { useAuth } from './contexts/AuthContext';

// Components
import Dashboard from './components/Dashboard/Dashboard';
import ChatPanel from './components/Chat/ChatPanel';
import ConnectionStatus from './components/UI/ConnectionStatus';
import ConfirmationIndicator from './components/UI/ConfirmationIndicator';
import RyderVision from './components/AI/RyderVision';
import SecurityAlertsPanel from './components/Security/SecurityAlertsPanel';
import NextcloudPanel from './components/Nextcloud/NextcloudPanel';
import ModuleRuntimeView from './components/ModuleWizard/ModuleRuntimeView';

// Testers
//import TestComponent from './TestComponent';
//import EndpointTester from './components/Debug/EndpointTester';
//import DataDebugger from './components/Debug/DataDebugger';


// Hooks
import { useBusinessData } from './hooks/useBusinessData';
import { useConfirmation } from './hooks/useConfirmation';

// Utils
import { DEFAULT_MODULES } from './utils/constants';
import apiService from './services/apiService';
import { theme, rgba } from './theme';

// Auth-role ranks (backend middleware/requireRole.js). Below manager a person sees only
// themselves and their site(s), and nothing that builds code (2026-10-05).
const ROLE_RANK = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
const isManagerRole = (role) => (ROLE_RANK[role] ?? -1) >= ROLE_RANK.manager;

const SentariOS = () => {
  const { user, logout } = useAuth();
  // The read-only demo account (the backend says so: user.isDemo from /auth/me and login,
  // decided by account id) sees every tab a manager sees, view only - the backend refuses
  // its changes. readRole is the role tab visibility uses; nothing here grants a write.
  const isDemo = user?.isDemo === true;
  const readRole = isDemo && !isManagerRole(user?.role) ? 'manager' : user?.role;
  const isManager = isManagerRole(readRole);
  // Core Platform State - below manager the landing page is My Home, not the dashboard
  const [activeModule, setActiveModule] = useState(() => (isManager ? 'dashboard' : 'my-home'));
  // Extra context for the destination page beyond just which key to render - e.g. a
  // dashboard tile click passing { initialDomain: 'finance' } to AIM. onModuleChange was
  // a bare string setter before this - no way for a click-through to say *which view* of
  // a page to land on, only which page. Cleared on every navigation (including a plain
  // sidebar click, which passes no params) so a later plain click never inherits stale
  // params from an earlier deep-linked one.
  //
  // Team Management deliberately does NOT get an initialFilter here - investigated
  // during implementation and found TeamPage's only filter is USER-ACCOUNT status
  // (active/pending/suspended/incomplete, i.e. login/invite state), not employment
  // status (Active/Inactive/On Leave, employees[].status - what the dashboard tiles are
  // actually about). Those are genuinely different concepts; passing a fake
  // { status: 'Active' } would either silently no-op or misleadingly filter by the
  // wrong kind of "active." Team-related tiles navigate to Team Management plainly
  // instead of deep-linking to a filter that doesn't really exist there.
  const [moduleNavParams, setModuleNavParams] = useState(null);
  const navigateToModule = useCallback((key, params = null) => {
    setActiveModule(key);
    setModuleNavParams(params);
  }, []);
  const [platformModules, setPlatformModules] = useState([]);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  // Argus tab: shown only when the backend says this account may view it - admins of a
  // business with the camera server configured, or the read-only sales demo account (one
  // tower's lenses, no server-admin link). GET /api/argus-ui answers 403/404 otherwise;
  // the backend decides who, so there is no role check here. null until it has answered.
  const argusUserKey = user?._id || user?.id || user?.email || null;
  const [argusAvailable, setArgusAvailable] = useState(null);
  useEffect(() => {
    if (!argusUserKey) { setArgusAvailable(false); return; }
    let live = true;
    apiService.request('/api/argus-ui')
      .then(d => { if (live) setArgusAvailable(!!d?.available); })
      .catch(() => { if (live) setArgusAvailable(false); });
    return () => { live = false; };
  }, [argusUserKey]);

  // User & Company State
  const [userName, setUserName] = useState(user?.profile?.firstName || user?.email || null);
  const [companyName, setCompanyName] = useState('');
  const [codeBuildingEnabled, setCodeBuildingEnabled] = useState(false);

  const toggleCodeBuilding = async () => {
    const newValue = !codeBuildingEnabled;
    setCodeBuildingEnabled(newValue);
    try {
      await apiService.request('/api/user-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ setting: 'codeBuildingEnabled', value: newValue })
      });
    } catch (e) {
      console.error('Failed to save setting:', e);
      setCodeBuildingEnabled(!newValue);
    }
  };

  const loadUserSettings = async () => {
    try {
      const data = await apiService.request('/api/user-settings');
      if (data.success) setCodeBuildingEnabled(data.settings.codeBuildingEnabled);
    } catch (e) { console.error('Failed to load user settings:', e); }
  };

  
  // Connection Status
  const [backendConnected, setBackendConnected] = useState(false);
  
  // File Analysis State
  const [fileAnalysisResult, setFileAnalysisResult] = useState(null);

  // Custom Hooks
  // Single source of truth for business data - Dashboard used to call this hook
  // independently too, meaning two separate fetch/30s-poll/visibility-listener loops
  // both hit /api/business-data whenever the dashboard was open. Now called once here
  // and passed down as props.
  const { businessData, isLoading: businessDataLoading, error: businessDataError, refreshData, derivedMetrics: businessDerivedMetrics } = useBusinessData();
  const { 
    activeConfirmation, 
    clearConfirmation,
    processConfirmationResponse 
  } = useConfirmation(userName, companyName);

  // Test backend connection
  const testBackendConnection = async () => {
    try {
      const response = await apiService.request('/api/health');
      
      if (response.status === 'healthy') {
        setBackendConnected(true);
        console.log('Backend connected successfully');
      } else {
        setBackendConnected(false);
        console.warn('Backend health check failed');
      }
    } catch (error) {
      setBackendConnected(false);
      console.error('Backend connection failed:', error);
    }
  };

  // Load existing modules from backend
  const loadExistingModules = useCallback(async () => {
    try {
      console.log('Loading existing modules...');
      
      const data = await apiService.getModules();
      
      if (data.success) {
        const builtModules = data.modules || [];
        const serverUserName = data.userName;
        
        console.log(`Loaded ${builtModules.length} persistent modules from backend`);
        
        const convertedModules = builtModules.map(module => ({
          id: module.id,
          name: module.name,
          type: 'ai-generated',
          status: 'active',
          description: module.description,
          features: module.features || [],
          created: module.created,
          hasGPS: module.hasGPS || false,
          originalType: module.originalType || module.type,
          completionStatus: module.completionStatus || null
        }));
        
        setPlatformModules(() => {
          const newTotal = [...DEFAULT_MODULES, ...convertedModules];
          console.log(`Total modules after reload: ${newTotal.length}`);
          return newTotal;
        });
        
        if (serverUserName && serverUserName !== userName) {
          setUserName(serverUserName);
        }
      } else {
        throw new Error(data.error || 'Failed to load modules');
      }
    } catch (error) {
      console.error('Failed to load existing modules:', error);
    }
  }, [userName]);

  // Initialize default modules
  const initializeDefaultModules = () => {
    setPlatformModules(prev => {
      const hasDefaults = prev.some(m => m.id === 'dashboard');
      if (hasDefaults) return prev;
      
      return DEFAULT_MODULES;
    });
  };

  // Comprehensive platform initialization
  const initializePlatform = async () => {
    console.log('Initializing Sentari OS Platform...');
    loadUserSettings();
    
    // Initialize default modules first
    initializeDefaultModules();
    
    // Test backend connection
    await testBackendConnection();

    // The module list (/api/modules) is manager and above; below manager the sidebar
    // shows no AI modules, so there is nothing to load.
    if (!isManager) {
      console.log('Platform initialization complete');
      return;
    }

    // Load modules - business data is NOT fetched here anymore. useBusinessData's own
    // internal useEffect already fetches on mount; explicitly calling refreshData()
    // here too meant every page load fired two near-simultaneous /api/business-data
    // requests (found live 2026-09-17, alongside the separate two-call-sites issue
    // this file's useBusinessData() comment already documents).
    await loadExistingModules();

    console.log('Platform initialization complete');
  };

  // Initialize platform on startup
  useEffect(() => {
    initializePlatform();
  }, []);

  // Handle file analysis
  const handleFileAnalyzed = useCallback((result) => {
    setFileAnalysisResult(result);
    setAiChatOpen(true);
  }, []);

  // Handle data changes from chat/AI operations
  const handleDataChanged = useCallback(() => {
    setTimeout(() => refreshData(), 500);
  }, [refreshData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setAiChatOpen(prev => !prev);
      }
      
      if (e.key === 'Escape') {
        if (aiChatOpen) {
          setAiChatOpen(false);
        } else if (activeConfirmation) {
          clearConfirmation();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [aiChatOpen, activeConfirmation, clearConfirmation]);

  // Module icon helper
  const getModuleIcon = (moduleId, moduleType) => {
    if (moduleId === 'dashboard') return BarChart3;
    if (moduleId === 'my-home') return Home;
    if (moduleId === 'business-setup') return MessageSquare;
    if (moduleId === 'module-library') return Code;
    if (moduleId === 'customization') return Palette;
    if (moduleId === 'aim') return ListChecks;
    if (moduleId === 'schedule') return CalendarClock;
    if (moduleId === 'argus') return Video;
    if (moduleType === 'ai') return Wrench;
    if (moduleType === 'ai-generated') return Zap;
    return Shield;
  };

  // Sidebar sections, real items first - see the sidebar-reorganization plan. Static
  // DEFAULT_MODULES entries carry a `group`; AI-generated modules and Nextcloud (its own
  // role list, unrelated to ROLE_RANK) are handled separately below.
  const NAV_GROUPS = [
    { key: 'overview', label: 'Overview' },
    { key: 'operations', label: 'Operations' },
    { key: 'monitoring', label: 'Monitoring' },
    { key: 'settings', label: 'Settings' },
  ];
  const MODULE_MIN_ROLE = {
    'team': 'manager',
    'aim': 'manager',
    'security-alerts': 'manager',
    'module-library': 'manager',
    'dashboard': 'manager',
    'financial-reports': 'manager',
    'clients': 'manager',
    'business-setup': 'manager',
    'customization': 'employee',
    // 'argus' has no role here: argusAvailable (the backend's answer) decides it
  };
  const canSeeModule = (m) => {
    if (m.status !== 'active') return false;
    // Generated modules are manager and above (their records are company-wide, and their
    // routes refuse reads below manager).
    if (m.type?.includes('ai') && !isManager) return false;
    // My Home is the below-manager landing page; managers have the dashboard instead.
    if (m.id === 'my-home') return !isManager;
    if (m.id === 'argus' && !argusAvailable) return false;
    // No real onboarding wizard exists behind this yet for anyone (flagged, not fixed) -
    // this only hides it once a business is clearly already onboarded (has at least one
    // employee on file), rather than showing a permanently-dead link forever.
    if (m.id === 'business-setup' && (businessData?.employees?.length ?? 0) > 0) return false;
    const minRole = MODULE_MIN_ROLE[m.id];
    if (minRole && (ROLE_RANK[readRole] ?? -1) < (ROLE_RANK[minRole] ?? 99)) return false;
    return true;
  };
  const renderModuleButton = (module) => {
    const IconComponent = getModuleIcon(module.id, module.type);
    const isBuilt = module.completionStatus?.routes && module.completionStatus?.database;
    return (
      <button
        key={module.id}
        onClick={() => navigateToModule(module.id)}
        style={{
          width: '100%',
          padding: '16px',
          marginBottom: '8px',
          background: activeModule === module.id
            ? rgba(theme.colors.primaryRgb, 0.2)
            : 'transparent',
          border: activeModule === module.id
            ? `1px solid ${rgba(theme.colors.primaryRgb, 0.5)}`
            : '1px solid transparent',
          borderRadius: '8px',
          color: activeModule === module.id ? theme.colors.primary : theme.colors.sidebarText,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          textAlign: 'left',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.2s ease'
        }}
      >
        <IconComponent size={20} />
        <span style={{ flex: 1 }}>{module.name}</span>
        {module.type?.includes('ai') && (
          <span style={{
            background: isBuilt ? 'rgba(34, 197, 94, 0.3)' : 'rgba(245, 158, 11, 0.3)',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            color: isBuilt ? '#22c55e' : '#f59e0b'
          }}>
            {isBuilt ? 'AI' : '⚠️'}
          </span>
        )}
      </button>
    );
  };
  const renderGroupHeader = (label) => (
    <div style={{
      color: theme.colors.sidebarText,
      fontSize: '11px',
      fontWeight: '700',
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      opacity: 0.5,
      margin: '16px 4px 8px',
    }}>
      {label}
    </div>
  );

  // Render active module content
  const renderActiveModule = () => {
    switch (activeModule) {
      case 'my-home':
      case 'dashboard':
        // Below manager the home page is My schedule / My profile (or company totals for
        // the analyst), never the Executive Dashboard.
        if (!isManager) {
          return (
            <MyHomePage
              businessData={businessData}
              isLoading={businessDataLoading}
              error={businessDataError}
              userName={userName}
              onNavigate={navigateToModule}
            />
          );
        }
        return (
          <Dashboard
            userName={userName}
            companyName={companyName}
            onModuleChange={setActiveModule}
            onNavigate={navigateToModule}
            onChatOpen={() => setAiChatOpen(true)}
            onFileAnalyzed={handleFileAnalyzed}
            businessData={businessData}
            isLoading={businessDataLoading}
            error={businessDataError}
            refreshData={refreshData}
            derivedMetrics={businessDerivedMetrics}
          />
        );
       case 'financial-reports': {
         const rr = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
         if ((rr[readRole] ?? -1) < rr.manager) { navigateToModule('dashboard'); return null; }
         return <FinancialReportingPage />;
       }
       case 'clients': {
         const rr = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
         if ((rr[readRole] ?? -1) < rr.manager) { navigateToModule('dashboard'); return null; }
         return <ClientsPage />;
       }
       case 'argus': {
         if (argusAvailable === null) return null;
         if (!argusAvailable) { navigateToModule('dashboard'); return null; }
         return <ArgusPage />;
       }
       case 'security-alerts':
        return <SecurityAlertsPanel />;
       case 'ryder':
        return <RyderVision />;
       case 'nextcloud':
        return <NextcloudPanel user={user} />;
       case 'team': {
         const rr = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
         if ((rr[readRole] ?? -1) < rr.manager) { navigateToModule('dashboard'); return null; }
         return <TeamPage />;
       }
       case 'aim': {
         const rr = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
         if ((rr[readRole] ?? -1) < rr.manager) { navigateToModule('dashboard'); return null; }
         return <AIMPage onOpenChat={() => setAiChatOpen(true)} initialDomain={moduleNavParams?.initialDomain} />;
       }
       case 'schedule':
         // Deliberately no role floor here, unlike 'aim'/'team' above - every
         // authenticated user sees at least their own schedule (SchedulePage resolves
         // the real per-user viewScope/canEdit server-side via GET /api/schedule/defaults).
         return <SchedulePage onOpenChat={() => setAiChatOpen(true)} />;
       case 'customization': {
         const rr = { viewer:0, analyst:1, employee:1, manager:2, admin:3, superadmin:4 };
         if ((rr[readRole] ?? -1) < rr.employee) { navigateToModule('dashboard'); return null; }
         return <CustomizationPage />;
       }
       case 'module-library': {
         if (!isManager) { navigateToModule('dashboard'); return null; }
         // Phase 1 scope (this business's own real modules only - cross-tenant sharing
         // needs new access-control design as a separate task, see the plan this was
         // built from): genuinely built = completionStatus really set, not just present -
         // this field was never written anywhere before today's fix, so filtering on it
         // now correctly excludes placeholder scaffolds (GPS Tracking, Geofence Tracker,
         // AI Training System, User Management) until someone actually finishes them.
         const builtModules = platformModules.filter(m =>
           m.type?.includes('ai') && m.completionStatus?.routes && m.completionStatus?.database
         );
         return <ModuleLibraryPage modules={builtModules} onOpen={navigateToModule} />;
       }
       default:
          // Check if this is an AI-generated module
          const aiModule = platformModules.find(m => 
            m.id === activeModule && m.type?.includes('ai')
          );
          
          if (aiModule && isManager) {
            // A generic, specs-driven view - not a per-module import/route. Any module
            // created or enhanced becomes reachable here automatically the moment its
            // route file exists (see backend/services/dynamicModuleRouter.js); this is
            // the only App.js edit that auto-wiring ever needs.
            return (
              <ModuleRuntimeView
                module={aiModule}
                onOpenChat={(prompt) => {
                  setAiChatOpen(true);
                }}
                onBack={() => navigateToModule('dashboard')}
              />
            );
          }
          
          return (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              background: `linear-gradient(135deg, ${rgba(theme.colors.primaryRgb, 0.1)}, ${rgba(theme.colors.accentRgb, 0.1)})`,
              minHeight: '100vh'
            }}>
              <h2 style={{ color: '#e2e8f0', fontSize: '28px', marginBottom: '16px' }}>
                Module Not Found
              </h2>
              <p style={{ color: theme.colors.textOnPageBackground, fontSize: '18px', marginBottom: '24px' }}>
                This module has not been built yet.
              </p>
              <button
                onClick={() => setAiChatOpen(true)}
                style={{
                  padding: '16px 32px',
                  background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
                  border: 'none',
                  borderRadius: '8px',
                  color: theme.colors.textOnPrimary,
                  fontSize: '16px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Ask AI to Build This
              </button>
            </div>
          );
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      // Fixed to the window so the sidebar and the page scroll separately (each has its own scroll area).
      height: '100vh', 
      overflow: 'hidden',
      background: `linear-gradient(135deg, ${theme.colors.shellBackground} 0%, ${theme.colors.shellBackgroundBlend} 50%, ${theme.colors.shellBackground} 100%)`,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      position: 'relative'
    }}>
      <ConnectionStatus isConnected={backendConnected} />
      <ConfirmationIndicator 
        activeConfirmation={activeConfirmation} 
        onClear={clearConfirmation} 
      />

      {/* Sidebar Navigation */}
      <div style={{
        width: '280px',
        background: rgba(theme.colors.sidebarBackgroundRgb, 0.95),
        backdropFilter: 'blur(20px)',
        borderRight: `1px solid ${rgba(theme.colors.primaryRgb, 0.2)}`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.3)',
        height: '100vh',
        flexShrink: 0
      }}>
        <div style={{
          padding: '24px',
          borderBottom: `1px solid ${rgba(theme.colors.primaryRgb, 0.2)}`,
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: theme.colors.primary,
            textShadow: `0 0 20px ${rgba(theme.colors.primaryRgb, 0.5)}`,
            marginBottom: '8px'
          }}>
            Sentari OS
          </h1>
          <p style={{ color: theme.colors.sidebarText, fontSize: '14px' }}>
            AI-Powered Business Platform
          </p>
          {userName && (
            <div style={{ 
              marginTop: '8px',
              padding: '4px 8px',
              background: 'rgba(34, 197, 94, 0.2)',
              borderRadius: '12px',
              color: '#22c55e',
              fontSize: '12px'
            }}>
              Welcome, {userName}!
            </div>
          )}
          {companyName && (
            <div style={{ 
              marginTop: '4px',
              padding: '4px 8px',
              background: 'rgba(245, 158, 11, 0.2)',
              borderRadius: '12px',
              color: '#f59e0b',
              fontSize: '12px'
            }}>
              {companyName}
            </div>
          )}
          {isManager && !isDemo && (
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            background: 'rgba(15, 23, 42, 0.6)',
            borderRadius: '8px',
            border: `1px solid ${rgba(theme.colors.primaryRgb, 0.2)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: theme.colors.sidebarText, fontSize: '12px' }}>🔧 Code Building</span>
            <button
              onClick={toggleCodeBuilding}
              style={{
                width: '36px', height: '20px', borderRadius: '10px',
                border: 'none', cursor: 'pointer',
                background: codeBuildingEnabled
                  ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                  : 'rgba(100, 116, 139, 0.4)',
                position: 'relative', transition: 'background 0.2s ease'
              }}
            >
              <div style={{
                width: '14px', height: '14px', borderRadius: '50%',
                background: 'white', position: 'absolute', top: '3px',
                left: codeBuildingEnabled ? '19px' : '3px',
                transition: 'left 0.2s ease'
              }} />
            </button>
          </div>
          )}
        </div>

        <div style={{ flex: 1, padding: '16px', overflowY: 'auto' }}>
          {NAV_GROUPS.map(group => {
            const items = (platformModules || []).filter(m => m.group === group.key && canSeeModule(m));
            // Monitoring's real content is one static item (Security Alerts) plus
            // Nextcloud, which isn't a platformModules entry at all - its own separate
            // role list (unrelated to ROLE_RANK), moved here from a standalone block at
            // the sidebar's bottom so real monitoring features are grouped together.
            const showNextcloud = group.key === 'monitoring' &&
              // Same list as the backend's routes/nextcloud.routes.js MANAGEMENT_ROLES.
              ['manager','admin','superadmin','ceo','chairman','division_manager','hr_director','finance','it_manager'].includes(readRole);
            if (items.length === 0 && !showNextcloud) return null;
            return (
              <div key={group.key}>
                {renderGroupHeader(group.label)}
                {items.map(renderModuleButton)}
                {showNextcloud && (
                  <button
                    onClick={() => navigateToModule('nextcloud')}
                    style={{
                      width: '100%',
                      padding: '16px',
                      marginBottom: '8px',
                      background: activeModule === 'nextcloud' ? rgba(theme.colors.primaryRgb, 0.2) : 'transparent',
                      border: activeModule === 'nextcloud' ? `1px solid ${rgba(theme.colors.primaryRgb, 0.5)}` : '1px solid transparent',
                      borderRadius: '8px',
                      color: activeModule === 'nextcloud' ? theme.colors.primary : theme.colors.sidebarText,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      textAlign: 'left',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>☁️</span>
                    <span style={{ flex: 1 }}>Nextcloud</span>
                  </button>
                )}
              </div>
            );
          })}

          {/* Modules: Module Library (real, built - see Part 5) plus this tenant's own
              AI-generated modules, grouped together since both answer "what modules exist." */}
          {(() => {
            const libraryEntry = (platformModules || []).find(m => m.id === 'module-library' && canSeeModule(m));
            const aiModules = (platformModules || []).filter(m => m.type?.includes('ai') && canSeeModule(m));
            if (!libraryEntry && aiModules.length === 0) return null;
            return (
              <div>
                {renderGroupHeader('Modules')}
                {libraryEntry && renderModuleButton(libraryEntry)}
                {aiModules.map(renderModuleButton)}
              </div>
            );
          })()}
        </div>

        <div style={{
          padding: '16px',
          borderTop: `1px solid ${rgba(theme.colors.primaryRgb, 0.2)}`,
          textAlign: 'center'
        }}>
          <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>
            Powered by Aetheris
          </div>
          <div style={{ 
            color: backendConnected ? '#22c55e' : '#ef4444', 
            fontSize: '12px', 
            fontWeight: '500' 
          }}>
            {backendConnected ? 'Backend Connected' : 'Backend Offline'}
          </div>
          <div style={{ color: theme.colors.primary, fontSize: '11px', marginTop: '2px' }}>
            Press Ctrl+K to open AI chat
          </div>
        </div>
        <button
          onClick={logout}
          style={{
            display:'block', margin:'8px 16px 16px', position:'sticky', bottom:'8px',
            padding:'10px',
            background:'rgba(20,10,10,0.95)', backdropFilter:'blur(10px)',
            border:'1px solid rgba(239,68,68,0.2)',
            borderRadius:'8px',
            color:'#f87171',
            cursor:'pointer',
            fontSize:'13px',
            fontWeight:'600',
            width:'calc(100% - 32px)',
          }}
        >
          Sign Out
        </button>
      </div>

      {/* Main Content Area */}
      <div style={{ flex: 1, height: '100vh', overflowY: 'auto' }}>
        {renderActiveModule()}
      </div>

      {/* Sticky AI Chat Button */}
      <button
        onClick={() => setAiChatOpen(!aiChatOpen)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: aiChatOpen
            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
            : `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent})`,
          border: 'none',
          color: aiChatOpen ? 'white' : theme.colors.textOnPrimary,
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: `0 8px 25px ${rgba(theme.colors.primaryRgb, 0.4)}`,
          zIndex: 1000,
          transition: 'all 0.3s ease'
        }}
        title={aiChatOpen ? 'Close Chat' : 'Open AI Chat (Ctrl+K)'}
      >
        {aiChatOpen ? <X size={24} /> : 'AI'}
      </button>

      {/* AI Chat Panel */}
      <ChatPanel
        isOpen={aiChatOpen}
        onClose={() => setAiChatOpen(false)}
        userName={userName}
        companyName={companyName}
        fileAnalysisResult={fileAnalysisResult}
        onDataChanged={handleDataChanged}
        onFileAnalyzed={setFileAnalysisResult}
      />
	{/*<div className="p-4 space-y-4">
  		<EndpointTester />
  		<DataDebugger />
	</div>*/}
    </div>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <Routes>
            <Route path="/login"           element={<LoginPage />} />
            <Route path="/activate"        element={<ActivatePage />} />
            <Route path="/change-password" element={<ForceChangePasswordPage />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <SentariOS />
              </ProtectedRoute>
            } />
          </Routes>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
